import React, { useEffect, useState } from 'react';
import { fiberAPI } from '../../services/api';
import { LoadingBlock, EmptyState } from '../../components/technical/TechnicalUI';
import { GitBranch, Router, Split, Wifi, ChevronDown, ChevronRight } from 'lucide-react';
import useDarkReady from '../../theme/useDarkReady';

// No GPS/lat-lng exists anywhere in this deployment (see fiber/tree — location/address are
// free-text only), so this renders the real OLT -> PON port -> splitter(s) -> ONU hierarchy
// as a collapsible node tree instead of a literal geo map.
function OnuLeaf({ onu }) {
  const online = onu.customer?.status === 'active';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0 6px 26px', fontSize: '.82rem' }}>
      <Wifi size={13} color={online ? 'var(--c-0ca30c)' : 'var(--c-9ca3af)'} />
      <span style={{ fontWeight: 500 }}>{onu.customer?.name || onu.onuSerial}</span>
      <span style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>{onu.onuSerial}</span>
    </div>
  );
}

function SplitterNode({ splitter, depth = 0 }) {
  const [open, setOpen] = useState(depth === 0);
  const count = splitter.onus.length + splitter.children.reduce((a, c) => a + countOnus(c), 0);
  return (
    <div style={{ marginLeft: depth > 0 ? 18 : 0, borderLeft: depth > 0 ? '1px dashed var(--cb-e5e7eb)' : 'none', paddingLeft: depth > 0 ? 12 : 0 }}>
      <div
        role="button" tabIndex={0} onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: '.85rem' }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Split size={14} color="var(--c-7c3aed)" />
        <span style={{ fontWeight: 600 }}>{splitter.name}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>{splitter.ratio} · {count} ONUs</span>
      </div>
      {open && (
        <div style={{ marginLeft: 22 }}>
          {splitter.children.map(c => <SplitterNode key={c._id} splitter={c} depth={depth + 1} />)}
          {splitter.onus.map(o => <OnuLeaf key={o._id} onu={o} />)}
        </div>
      )}
    </div>
  );
}

function countOnus(splitter) {
  return splitter.onus.length + splitter.children.reduce((a, c) => a + countOnus(c), 0);
}

function PortNode({ port }) {
  const [open, setOpen] = useState(false);
  const total = port.splitters.reduce((a, s) => a + countOnus(s), 0) + port.unmappedOnus.length;
  return (
    <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 10, marginBottom: 10, background: 'var(--cb-ffffff)', overflow: 'hidden' }}>
      <div
        role="button" tabIndex={0} onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer' }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Router size={16} color="var(--primary)" />
        <span style={{ fontWeight: 700, fontSize: '.9rem' }}>PON Port {port.ponPort}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: '.78rem', marginLeft: 'auto' }}>{port.splitters.length} splitters · {total} ONUs</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 12px' }}>
          {port.splitters.map(s => <SplitterNode key={s._id} splitter={s} />)}
          {port.unmappedOnus.map(o => <OnuLeaf key={o._id} onu={o} />)}
          {port.splitters.length === 0 && port.unmappedOnus.length === 0 && (
            <div style={{ color: 'var(--text-muted)', fontSize: '.8rem', padding: '6px 0' }}>No splitters or ONUs mapped to this port</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TechnicalNetworkMapPage() {
  useDarkReady();
  const [ports, setPorts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fiberAPI.getTree().then(({ data }) => setPorts(data.data.ports || [])).catch(() => setPorts([])).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingBlock />;
  if (!ports || ports.length === 0) return <EmptyState icon={GitBranch} text="No PON ports mapped yet" />;

  return (
    <div>
      <div className="page-header">
        <h2>Network Map</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>OLT → PON port → splitter → ONU topology</p>
      </div>
      {ports.map(p => <PortNode key={p.ponPort} port={p} />)}
    </div>
  );
}
